









































































































import { Vue, Component } from "vue-property-decorator";
import { namespace } from "vuex-class";
import SubscriptionBox from "@/modules/activation/components/SubscriptionBox.vue";
import {
  StripeCreditCard,
  SubscriptionPayload,
  SubscriptionPlan,
} from "@/modules/subscriptions/types";
import { loadStripe, Stripe, StripeCardElement } from "@stripe/stripe-js";
import { AxiosResponse } from "axios";
import CreditCardTable from "@/modules/subscriptions/components/CreditCardTable.vue";
import { User } from "@/modules/auth/types";

const Auth = namespace("auth");
const Subscription = namespace("subscriptions");
@Component({
  components: {
    "subscription-box": SubscriptionBox,
    "credit-card-table": CreditCardTable,
  },
})
export default class SelectPlan extends Vue {
  @Auth.Getter("getUser") user!: User;

  @Subscription.Action("getAllSubscriptionPlans")
  getAllSubscriptionPlans!: () => Promise<{}>;
  @Subscription.Getter("getAllCreditCards") creditCards: StripeCreditCard[];

  @Subscription.Action("createSubscription") createSubscription!: (
    payload: SubscriptionPayload
  ) => Promise<AxiosResponse>;

  @Subscription.Action("provisionSubscription")
  provisionSubscription!: () => Promise<AxiosResponse>;

  @Subscription.Getter("allSubscriptionPlans")
  allSubscriptionPlans: SubscriptionPlan[];

  @Subscription.Action("getCurrentVendorQuota")
  getCurrentVendorQuota!: () => Promise<{}>;

  @Subscription.Action("switchDefaultCard")
  switchDefaultCard!: (cardId: string) => Promise<AxiosResponse>;

  @Subscription.Action("fetchAllCards")
  fetchAllCards!: () => Promise<AxiosResponse>;

  card: StripeCardElement;
  stripe: Stripe;
  currentPlan: SubscriptionPlan = {
    amlEnabled: false,
    amlQuota: 0,
    billingCurrencyCode: "usd",
    billingInterval: "month",
    baseBillingUnitAmount: 5000,
    enabled: true,
    id: "",
    name: "Startup",
    stripePriceId: "",
    tier: 0,
    verificationEnabled: true,
    verificationQuota: 100,
  };
  selectedCard: StripeCreditCard = {
    brand: "",
    cvcCheck: "",
    default: false,
    expireMonth: 0,
    expireYear: 0,
    fingerprint: "",
    id: "",
    last4: "",
    stripeId: "",
    vendorId: "",
  };
  isRetrying = false;
  loading = false;

  created() {
    this.$mixpanel.track("Select Plan Started", {
      ...this.user,
    });
  }
  async beforeMount() {
    // Get all plans and select the first one as default
    await this.getAllSubscriptionPlans();
    const firstPlan = this.allSubscriptionPlans[0];
    this.selectPlan(firstPlan);

    const { VUE_APP_STRIPE_API, VUE_APP_STRIPE_STAGING_API } = process.env;

    this.stripe = (await loadStripe(
      VUE_APP_STRIPE_API ? VUE_APP_STRIPE_API : VUE_APP_STRIPE_STAGING_API
    )) as Stripe;

    if (this.stripe) {
      const elements = this.stripe.elements();
      // Card Element styles
      const style = {
        base: {
          fontSize: "16px",
          color: "#32325d",
          fontFamily:
            "-apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif",
          fontSmoothing: "antialiased",
          "::placeholder": {
            color: "#a0aec0",
          },
        },
      };
      this.card = elements.create("card", { style: style });
      this.card.mount("#card-element");
      this.card.on("focus", function () {
        const el = document.getElementById("card-element-errors");
        if (el) {
          el.classList.add("focused");
        }
      });

      this.card.on("blur", function () {
        const el = document.getElementById("card-element-errors");
        if (el) {
          el.classList.add("focused");
        }
      });

      this.card.on("change", ({ error }) => {
        const displayError = document.getElementById(
          "card-element-errors"
        ) as HTMLElement;
        if (displayError) {
          if (error) {
            displayError.innerHTML = `<b>Error!</b> ${error.message}`;
          } else {
            displayError.innerHTML = "";
          }
        }
      });
    }
  }

  async switchCard(card: StripeCreditCard) {
    this.selectedCard = card;
    const result = await this.switchDefaultCard(this.selectedCard.id);
    if (result.data) {
      const { last4 } = result.data.data;
      this.$notify({
        group: "success",
        title: "Default Card Changed",
        text: `The card ending ${last4} is now the default card.`,
      });
    }
  }

  async subscribeToPlan() {
    // Fetch all cards and set default card to the selected card
    if (this.selectedCard.stripeId === "") {
      await this.fetchAllCards();
      const filterResult = this.creditCards.filter(
        (card) => card.default === true
      );
      if (filterResult.length !== 0) {
        if (filterResult[0]) this.selectedCard = filterResult[0];
      }
    }

    this.loading = true;
    if (this.stripe) {
      this.createSubscription({
        paymentMethodId: this.selectedCard.stripeId,
        planId: this.currentPlan.id,
      })
        .then(() => {
          this.$notify({
            group: "success",
            title: "Success",
            text: "Subscribed to Plan!",
          });
          this.loading = false;
          this.$router.push({ name: "congratulations" });
        })
        .catch(() => {
          this.loading = false;
          this.$notify({
            group: "error",
            title: "Error",
            text: "Failed to subscribe to plan.",
          });
        });
    }
  }
  confirmCreateSubscription(payload: SubscriptionPayload) {
    const { paymentMethodId } = payload;
    this.createSubscription(payload)
      .then((response) => {
        return response.data;
      })
      // If the card is declined, display an error to the user.
      .then((result) => {
        if (!result.isSuccessful) {
          // The card had an error when trying to attach it to a customer
          const error = {
            error: {
              message: result.errors[0].message,
            },
          };

          throw error;
        }

        return result;
      })
      // Normalize the result to contain the object returned
      // by Stripe. Add the addional details we need.
      .then((result) => {
        return {
          // Use the Stripe 'object' property on the
          // returned result to understand what object is returned.
          subscription: result,
          paymentMethodId: paymentMethodId,
        };
      })
      // Some payment methods require a customer to do additional
      // authentication with their financial institution.
      // Eg: 2FA for cards.
      .then(this.requiresCustomerAction)
      // If attaching this card to a Customer object succeeds,
      // but attempts to charge the customer fail. You will
      // get a requires_payment_method error.
      .then(this.requiresPaymentMethod)
      // No more actions required. Provision your service for the user.
      .then(this.onSubscriptionComplete)
      .catch(() => {
        // An error has happened. Display the failure to the user here.
        // We utilize the HTML element we created.
      });
  }
  requiresCustomerAction({ subscription, paymentMethodId }) {
    const stripeData = JSON.parse(subscription.data.stripeData);
    const paymentIntent = stripeData.latest_invoice.payment_intent;

    if (stripeData.status === "active") {
      // subscription was successfull, move on
      return {
        subscription: subscription,
        paymentMethod: paymentMethodId,
        lastPaymentIntent: {
          paymentIntent,
        },
      };
    }

    if (
      paymentIntent.status === "requires_action" ||
      (this.isRetrying && paymentIntent.status === "requires_payment_method")
    ) {
      return this.stripe
        .confirmCardPayment(paymentIntent.client_secret, {
          //eslint-disable-next-line
          payment_method: paymentMethodId,
        })
        .then((result) => {
          if (result.error) {
            // start code flow to handle updating the payment details
            // Display error message in your UI.
            // The card was declined (i.e. insufficient funds, card has expired, etc)
            throw result;
          } else {
            const { paymentIntent } = result;
            if (paymentIntent && paymentIntent.status === "succeeded") {
              // There's a risk of the customer closing the window before callback
              // execution. To handle this case, set up a webhook endpoint and
              // listen to invoice.paid. This webhook endpoint returns an Invoice.

              // The payment was challenged and authorized
              return {
                subscription: subscription,
                paymentMethod: paymentMethodId,
                lastPaymentIntent: result,
              };
            } else {
              return {
                subscription: subscription,
                paymentMethod: paymentMethodId,
              };
            }
          }
        });
    } else {
      // No customer action needed
      return {
        subscription: subscription,
        paymentMethod: paymentMethodId,
      };
    }
  }

  requiresPaymentMethod({ subscription, paymentMethod, lastPaymentIntent }) {
    if (lastPaymentIntent && lastPaymentIntent.status === "succeeded") {
      // retry payment was successful
      // continue
      return { subscription: subscription, paymentMethod: paymentMethod };
    }

    const stripeData = JSON.parse(subscription.data.stripeData);

    // subscription is active, no customer actions required.
    if (subscription.data.status === "active") {
      return { subscription: subscription, paymentMethod: paymentMethod };
    } else if (
      stripeData.latest_invoice.payment_intent.status ===
      "requires_payment_method"
    ) {
      // We set isRetrying to true to try to confirm payment
      this.isRetrying = true;

      // An incomplete subscription exists.
      // We can retry completing the subscription with updated card info.
      throw { error: { message: "Your card was declined." } };
    } else {
      return { subscription: subscription, paymentMethod: paymentMethod };
    }
  }
  onSubscriptionComplete() {
    this.loading = false;
    this.getCurrentVendorQuota();
    this.provisionSubscription();
    this.isRetrying = false;
    this.$router.push({ name: "congratulations" });
  }
  selectPlan(plan: SubscriptionPlan) {
    this.currentPlan = plan;
  }
  getVerificationPrice(subscriptionPlanName) {
    switch (subscriptionPlanName) {
      case "Startup":
        return "1.25";
      case "Growth":
        return "1.00";
      case "Scale":
        return "0.85";
    }
  }
  getSubscriptionCost(subscriptionCost) {
    switch (subscriptionCost) {
      case "Startup":
        return "99";
      case "Growth":
        return "199";
      case "Scale":
        return "599";
    }
  }
}
